/* Copyright (c) 2021 Caring Transitions Online Auctions. All Rights Reserved. */

// library imports
import { Component, createRef } from 'react';
import { Row, Form, Col, Button } from 'react-bootstrap';
import moment from 'moment';
import * as momentTz from 'moment-timezone';
import axios from 'axios';

// project imports
import { getSellerUrl, getSellerHeaders } from 'shared/http';
import { ArrayValueReorder } from 'shared/modifiers';
import { locationlist, statelist, citylist } from 'shared/settings/apiconfig';
import Datatable from 'shared/datatable';

// style imports
import './LocationList.scss';

/**
 * this page renders the table to list locations fo the user
 * 
 * @param props
 * 
 * @returns html code to render location list table
 * 
**/

export class LocationList extends Component {

    config;
    initialSearchState;

    constructor(props) {
        super(props);

        this.dataTable = createRef();

        /* initial search state */
        this.initialSearchState = {
            title: { field: 'title', value: '', op: 'LIKE', join: 'AND' },
            contact: { field: 'contactname', value: '', op: 'LIKE', join: 'AND' },
            city: { field: 'city', value: '', op: 'LIKE', join: 'AND' },
            state: { field: 'state', value: '', op: 'LIKE', join: 'AND' }
        }

        this.state = {
            search: this.initialSearchState,
            searchData: false,
            tableData: [],
            issearch: false,
            stateList: [],
            cityList: []
        }

        this.config = {
            name: 'location',
            url: locationlist,
            params: {},
            columns: [
                {
                    name: '', property: 'id', columnType: 'actions', actions: ['edit'], customHeader: <></>, width: '50px',
                    actionEvents: { edit: (row, state) => { this.props.history.push({ pathname: `/locations/edit/${row['id']}`, state: state }) } }
                },
                { name: 'Title', property: 'title', sort: true, width: '250px', columnType: 'custom', customColumn: this.customTitle },
                { name: 'Contact', property: 'contactname', sort: true, width: '130px' },
                { name: 'State', property: 'state', sort: true, width: '130px' },
                { name: 'City', property: 'city', sort: true, width: '130px' },
                { name: 'Status', property: 'status', sort: true, width: '130px' },
                { name: 'Last Modified', property: 'modifieddate', sort: true, width: '160px', columnType: 'custom', customColumn: this.customDate }
            ],
            sort: [{ field: 'title', direction: 'asc' }],
            defaultFilter: [
                {
                    "field": "status",
                    "value": "active",
                    "op": "=",
                    "join": "OR"
                },
                {
                    "field": "status",
                    "value": "Inactive",
                    "op": "=",
                    "join": "OR"
                }        
            ],
            pagination: true,
            field: ['id', 'title', 'website', 'contactname', 'state', 'city', 'status', 'modifieddate'],
            ignoreRowBackground: true
        };

        if (props.location.state && props.location.state.datatable && props.location.state.datatable.search) {
            let tempSearch = { ...this.initialSearchState };
            const search = [...props.location.state.datatable.search];

            search.forEach(element => {
                let temp = { ...element };
                if (typeof temp['value'] === 'string') {
                    temp['value'] = temp['value'].replace(/(^\*)|(\*$)/g, '');
                }
                tempSearch[temp['field']] = temp;
            });

            this.state['search'] = tempSearch;
            this.state['issearch'] = Boolean(search.length);
            this.state['searchData'] = true;
            if (this.state['search']['state'].value)
                this.getCity();
            this.enableSearch();
        }
    }

    customTitle = (values) => {
        return (
            <div className="d-flex flex-column text-wrap" style={{ width: '100%', height: '100%', padding: '8px 0' }}>
                <span>{values['title']}</span>
                <small className="text-muted text-break">{values['website']}</small>
            </div>
        );
    };

    // handle enter key search
    _handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            this.enableSearch()
        }
    }

    customDate = (value) => {
        if (value['modifieddate']) {
            return `${moment.utc(value['modifieddate']).local().format('MM/DD/YYYY hh:mm A')} ${momentTz.tz(momentTz.tz.guess()).zoneAbbr()}`;
        }
        return '- - -';
    };

    componentDidMount() {
        this.getStateList();
        window.history.pushState(null, null, window.location.pathname);
        window.addEventListener('popstate', this.onBack); 
    }

    onBack = (e) => {
        e.preventDefault();
        window.history.pushState(null, null, window.location.pathname);
    }

    /* Get state list */
    getStateList = () => {
        axios.get(getSellerUrl(statelist), getSellerHeaders())
            .then(response => {
                this.stateList = ArrayValueReorder(response.data.data, 'state');
                this.setState({ stateList: this.stateList });
            })
            .catch(error => {
            });
    }

    /* called when search input changes */
    searchInputChange = (event) => {
        let eventValue = event.target.value;
        this.setState({
            ...this.state,
            issearch: true,
            search: {
                ...this.state.search,
                [event.target.name]: {
                    ...this.state.search[event.target.name],
                    value: eventValue
                }
            }
        }, () => {
            if (eventValue === ''){
                this.checkSearch();
                this.checkSearch();
                this.setState({
                    search: {
                        ...this.state.search,
                        city: {
                            ...this.state.search.city,
                            value: ''
                        }
                    }
                });}
            if (event.target.name === 'state') {
                this.getCity();
            }
        });
    }

    /* called when clear all in search clicked */
    resetState = () => {
        let issearchData = this.state.searchData;
        this.setState({
            ...this.state,
            search: this.initialSearchState,
            searchData: false,
            issearch: false,
        }, () => {
            if (issearchData)
                this.dataTable.current.searchData();
        });
    }

    /* called when search button clicked */
    enableSearch = () => {
        let search = {};
        search = {
            title: { ...this.state.search.title, value: this.state.search.title.value ? '*' + this.state.search.title.value + '*' : '' },
            contact: { ...this.state.search.contact, value: this.state.search.contact.value ? '*' + this.state.search.contact.value + '*' : '' },
            city: { ...this.state.search.city, value: this.state.search.city.value ? this.state.search.city.value : '' },
            state: { ...this.state.search.state, value: this.state.search.state.value ? this.state.search.state.value : '' }
        }
        this.setState({
            filterSearch: search,
            searchData: true,
        }, () => {
            this.dataTable.current.searchData();
        });
    }

    /* check enable search and clear all */
    checkSearch = () => {
        let isenable = false;
        let issearchData = this.state.searchData;
        for (let control of Object.keys(this.state.search)) {
            if (this.state.search[control].value) {
                isenable = true;
                break;
            }
        }
        this.setState({
            ...this.state,
            search: isenable ? this.state.search : this.initialSearchState,
            searchData: isenable ? this.state.searchData : false,
            issearch: isenable
        }, () => {
            if (!isenable && issearchData)
                this.dataTable.current.searchData();
        });
    }

    /* get city list */
    getCity = () => {
        let cityList = [];
        if (this.state.search.state.value) {
            axios.get(getSellerUrl(`${citylist}/${this.state.search.state.value}`), getSellerHeaders())
                .then(response => {
                    cityList = ArrayValueReorder(response.data.data, 'city');
                    cityList = cityList.filter((city, index) => {
                        const _city = JSON.stringify(city);
                        return index === cityList.findIndex(obj => {
                            return JSON.stringify(obj) === _city;
                        });
                    });
                    this.setState({
                        cityList: cityList,
                        search: {
                            ...this.state.search,
                            city: {
                                ...this.state.search.city,
                                value: ''
                            }
                        }
                    });
                })
                .catch(error => {
                });
        }
    }

    /* called when table data changed */
    updateTableData = (event) => {
        this.setState({
            ...this.state,
            tableData: event
        });
    }


    render() {

        return (
            <div style={{ overflow: "auto", overflowY: "overlay" }}>
                <div className="location-search">
                    <div className="location-list-search">
                        <Form className="location-list-font">
                            <div className="location-search-container">
                                <Row>
                                    <Col>
                                        <label>Title</label>
                                        <Form.Control onKeyDown={this._handleKeyDown} type="text" name="title" value={this.state.search.title.value} onChange={(event) => this.searchInputChange(event)} />
                                    </Col>

                                    <Col>
                                        <label>Contact Name</label>
                                        <Form.Control onKeyDown={this._handleKeyDown} type="text" name="contact" value={this.state.search.contact.value} onChange={(event) => this.searchInputChange(event)} />
                                    </Col>
                                    <Col>
                                        <label>State</label>
                                        <Form.Control as="select" name="state" value={this.state.search.state.value} onChange={(event) => this.searchInputChange(event)}>
                                            <option value="" >All</option>
                                            {
                                                this.state.stateList.map((state, idx) => <option key={idx} value={state['state']}>{state['state']}</option>)
                                            }
                                        </Form.Control>
                                    </Col>
                                    <Col>
                                        <label>City</label>
                                        <Form.Control as="select" name="city" value={!this.state.search.state.value ? !this.state.search.city.value : this.state.search.city.value} disabled={!this.state.search.state.value} style={{textTransform: "capitalize"}} onChange={(event) => this.searchInputChange(event)}>
                                            <option value="" >All</option>
                                            {this.state.cityList.map((city, index) =>
                                                <option key={index} value={city.city}>{city.city}</option>
                                            )}
                                        </Form.Control>
                                    </Col>
                                </Row>
                                <Row className="d-flex" style={{ marginBottom: "-1%" }}>
                                    <Col md={6}>
                                    </Col>
                                    <Col md={6}>
                                        <div className="buttonClass">
                                            <Button className="clear-button" onClick={() => this.resetState()}>Clear All</Button>
                                            <Button variant="seller-search" onClick={() => this.enableSearch()}>Search</Button>
                                        </div>
                                    </Col>
                                </Row>
                            </div>
                        </Form>
                    </div>
                </div>
                <div className="location-list">
                {/* need to add props, as it provides the previous state of datatable */}
                <Datatable {...this.props} config={{ ...this.config }} search={this.state.searchData ? { search: { ...this.state.filterSearch }, searchData: this.state.searchData } : null} ref={this.dataTable} onDataUpdate={this.updateTableData}></Datatable>
            </div>
            </div>
        );
    }
}